import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useSelector} from 'react-redux'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import {CircularProgress, Divider} from '@mui/material'
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded'
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded'
import {SaveButton, CancelButton} from '../ProjectDetails/ProjectDetailStyle'
import SnackbarComponent from '../common/Snackbar/SnackbarComponent'
import ConfirmationDialog from '../ProjectDetails/UseCases/ConfirmationDialog'
import UseCaseNotificationAlert from '../ProjectDetails/UseCases/UseCaseNotificationAlert'
import {
  StyledFlexBox,
  StyledNotificationInfoOutlinedIcon,
  StyledNotificationTextBox
} from '../ProjectDetails/UseCases/UseCaseStyles'
import CustomizationDialog from '../customSettings/Customization/CustomizationDialog'
import CancelPopup from '../customSettings/CancelPopup'
import {getFormattedCustomLabelsRequest} from '../customSettings/Customization/helper'
import {useConfig} from '../../store/hooks/config'
import {selectFilterData} from '../../store/slices/adminPermissionSlice'
import {useAdminPermission} from '../../store/hooks/adminPermission'
import {useUpdateCustomSettings} from '../../store/hooks/customSettings'
import {
  selectedJurisdictionChanged,
  selectedDataAggregationChanged,
  selectedTrendAnalysisChanged,
  selectedUserFeedbackChanged,
  selectedChatbotChanged,
  selectedExportToggleChanged,
  selectedBenefitsChanged,
  selectedWorkforcesChanged,
  selectedPOIChanged,
  selectedPersonaChanged,
  selectedUseCasesChanged,
  selectedChatbotExportChanged,
  selectedChatbotCopyChanged
} from '../ProjectDetails/Helper/CompareChanges'
import {
  getSelectedUseCases,
  handleUpdateSelectedUseCases,
  updateSelectedData
} from '../ProjectDetails/Helper/UseCaseHelper'
import {StyledTitle} from './ManageMyPermissionsStyle'
import ProjectSectionAccordion from '../ProjectDetails/common/ProjectSectionAccordion/ProjectSectionAccordianComponent'
import SectionGroupHeader from '../ProjectDetails/common/SectionGroupHeader'
import UserFeedbackComponent from '../ProjectDetails/UserFeedback/UserFeedback'
import JurisdictionComponent from '../ProjectDetails/Jurisdiction/JurisdictionComponent'
import DataAggregation from '../ProjectDetails/DataAggregation/DataAggregation'
import IndividualProfileComponent from '../ProjectDetails/IndividualProfile/IndividualProfileComponent'
import IndividualHealthFilterComponent from '../ProjectDetails/IndividualHealthFilter/IndividualHealthFilterComponent'
import FinancialWellbeingComponent from '../ProjectDetails/FinancialWellbeing/FinancialWellbeingComponent'
import HomeAndHouseholdFilterComponent from '../ProjectDetails/HomeAndHouseholdFilters/HomeAndHouseholdFilterComponent'
import ResourceAccessBarriersComponent from '../ProjectDetails/ResourceAccessBarriers/ResourceAccessBarriersComponent'
import CommunitySupportComponent from '../ProjectDetails/CommunitySupport/CommunitySupportComponent'
import EnvironmentalExposureComponent from '../ProjectDetails/EnvironmentalExposure/EnvironmentalExposureComponent'
import Benefits from '../ProjectDetails/Benefits'
import PointOfInterest from '../ProjectDetails/PointOfInterest/PointOfInterest'
import UseCases from '../ProjectDetails/UseCases'
import PersonaFlag from '../ProjectDetails/PersonaFlag/PersonaFlag'
import TrendAnalysisComponent from '../ProjectDetails/TrendAnalysis/TrendAnalysis'
import ExportPermissionComponent from '../ProjectDetails/ExportPermissionComponent/ExportPermissionComponent'
import ChatbotComponent from '../ProjectDetails/chatbot/ToggleChatbot'
import ChatbotExport from '../ProjectDetails/chatbot/ChatbotExport'
import ChatbotCopy from '../ProjectDetails/chatbot/ChatbotCopy'
import Workforces from '../ProjectDetails/Workforces'

const EXPANDABLE_SECTIONS = {
  userFeedbackMode: false,
  jurisdictions: false,
  dataGranularity: false,
  individualProfile: false,
  individualHealth: false,
  financialWellbeing: false,
  homeAndHousehold: false,
  resourceAccessBarriers: false,
  communitySupport: false,
  environmentalExposure: false,
  benefitPrograms: false,
  pointOfInterests: false,
  commonUsecases: false,
  behavioralPersona: false,
  trendAnalysis: false,
  export: false,
  healthprismAgent: false,
  workforces: false
}

const getExpandedState = (expanded) =>
  Object.keys(EXPANDABLE_SECTIONS).reduce((acc, key) => {
    acc[key] = expanded
    return acc
  }, {})

const getFiltersArray = (filterDataList) => {
  if (Array.isArray(filterDataList)) {
    return filterDataList
  }
  if (filterDataList?.adminPermission?.filters) {
    return filterDataList.adminPermission.filters
  }
  if (filterDataList?.filters) {
    return filterDataList.filters
  }
  return []
}

const ManageMyPermissions = () => {
  const navigate = useNavigate()
  const filterData = useSelector(selectFilterData)

  const {
    jurisdiction,
    dataAggregationConfig,
    benefits,
    workforces,
    useCaseConfigData,
    pointOfInterestsData,
    currentBenefits,
    currentJurisdiction,
    currentWorkforces,
    currentUseCases,
    currentPOI,
    currentTrendAnalysisFlag,
    currentUserFeedbackFlag,
    currentChatbotFlag,
    currentChatbotExportFlag,
    currentChatbotCopyFlag,
    currentExportFlag,
    currentPersonaFlag,
    currentDataAggregation,
    loadingUpdatedPermission,
    errorUpdatedPermission,
    filterDetailsError,
    loadingHealthAndEquityConditions,
    errorLoadingHealthAndEquityConditions,
    loadingStates,
    errorLoadingStates,
    reloadFilters,
    updatedLabelLoading,
    customizationData,
    updateCustomizationLabels,
    updatedLabelError,
    setRefreshDashboard,
    updateCustomizationWithDefaultData
  } = useAdminPermission()

  const {handleUpdate} = useUpdateCustomSettings()
  const {personasConfig, filterConfig} = useConfig()

  const [expandedSections, setExpandedSections] = useState(EXPANDABLE_SECTIONS)

  const [selectedJurisdiction, setSelectedJurisdiction] = useState([])
  const [selectedDataAggregation, setSelectedDataAggregation] = useState([])
  const [selectedFilters, setSelectedFilters] = useState([])
  const [selectedPOI, setSelectedPOI] = useState([])
  const [selectedBenefits, setSelectedBenefits] = useState([])
  const [selectedWorkforces, setSelectedWorkforces] = useState([])
  const [selectedTrendAnalysis, setSelectedTrendAnalysis] = useState([0])
  const [selectedChatbot, setSelectedChatbot] = useState([0])
  const [selectedChatbotExport, setSelectedChatbotExport] = useState([0])
  const [selectedChatbotCopy, setSelectedChatbotCopy] = useState([0])
  const [selectedExportToggle, setSelectedExportToggle] = useState([0])
  const [selectedPersona, setSelectedPersona] = useState([0])
  const [selectedUserFeedback, setSelectedUserFeedback] = useState([0])
  const [closeExistingEventFlag, setCloseExistingEventFlag] = useState(false)

  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true)
  const [customLabelDialogProps, setCustomLabelDialogProps] = useState({open: false, section: '', data: []})
  const [selectedUseCases, setSelectedUseCases] = useState([])
  const [updatedUseCases, setUpdatedUseCases] = useState([])
  const [affectedUseCases, setAffectedUseCases] = useState([])
  const [filterToBeDeleted, setFilterToBeDeleted] = useState({})
  const [showUseCaseConfirmationDialog, setShowUseCaseConfirmationDialog] = useState(false)
  const [showNotification, setShowNotification] = useState(false)
  const [showInfoAlert, setShowInfoAlert] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLabelCustomized, setIsLabelCustomized] = useState(false)
  const [open, setOpen] = useState(false)
  const [navigateToAdminPanel, setNavigateToAdminPanel] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const lastHydratedStateKey = useRef('')

  const hasAnyFilters = useMemo(() => selectedFilters.length > 0, [selectedFilters])
  const areAllSectionsExpanded = Object.values(expandedSections).every(Boolean)

  const filtersArray = useMemo(() => getFiltersArray(filterData), [filterData])

  const permissionedFilters = useMemo(
    () => filtersArray.filter((option) => option.IS_PERMISSIONED === true || option.IS_PERMISSIONED === 'true'),
    [filtersArray]
  )

  const permissionedFilterKeys = useMemo(
    () =>
      permissionedFilters
        .map((option) => option.FILTER_KEY)
        .sort((a, b) => a.localeCompare(b))
        .toString(),
    [permissionedFilters]
  )

  const isBaseDataReady = useMemo(
    () =>
      Boolean(filterData) &&
      Array.isArray(jurisdiction) &&
      jurisdiction.length > 0 &&
      Array.isArray(pointOfInterestsData) &&
      pointOfInterestsData.length > 0 &&
      Array.isArray(dataAggregationConfig?.data) &&
      dataAggregationConfig.data.length > 0,
    [filterData, jurisdiction, pointOfInterestsData, dataAggregationConfig]
  )

  const initialStateKey = useMemo(
    () =>
      JSON.stringify({
        permissionedFilterKeys,
        currentJurisdiction,
        currentDataAggregation,
        currentPOI,
        currentBenefits,
        currentWorkforces,
        currentUseCases,
        currentTrendAnalysisFlag,
        currentUserFeedbackFlag,
        currentChatbotFlag,
        currentChatbotExportFlag,
        currentChatbotCopyFlag,
        currentExportFlag,
        currentPersonaFlag
      }),
    [
      permissionedFilterKeys,
      currentJurisdiction,
      currentDataAggregation,
      currentPOI,
      currentBenefits,
      currentWorkforces,
      currentUseCases,
      currentTrendAnalysisFlag,
      currentUserFeedbackFlag,
      currentChatbotFlag,
      currentChatbotExportFlag,
      currentChatbotCopyFlag,
      currentExportFlag,
      currentPersonaFlag
    ]
  )

  const handleExpandAllToggle = useCallback(() => {
    const newExpandedState = !areAllSectionsExpanded
    setExpandedSections(getExpandedState(newExpandedState))
  }, [areAllSectionsExpanded])

  const handleAccordionChange = useCallback(
    (sectionKey) => (_, isExpanded) => {
      setExpandedSections((prev) => ({
        ...prev,
        [sectionKey]: isExpanded
      }))
    },
    []
  )

  const handleOptionDeletion = (type, data, option) => {
    setFilterToBeDeleted({type, data, option})
    const selectedUseCasesList = []
    for (const item of updatedUseCases || []) {
      for (const topic of item.topics) {
        handleUpdateSelectedUseCases(topic, selectedUseCasesList, option)
      }
    }
    setAffectedUseCases([...selectedUseCasesList])
    if (selectedUseCasesList.length > 0) {
      setShowUseCaseConfirmationDialog(true)
    } else {
      updateSelectedData(type, data, option, setSelectedFilters)
    }
  }

  const handleConfirmOptionDeletion = () => {
    const {type, data, option} = filterToBeDeleted || {}
    updateSelectedData(type, data, option, setSelectedFilters)
    setShowUseCaseConfirmationDialog(false)
    setShowInfoAlert(true)
  }

  const handleCancel = () => {
    if (isSaveDisabled) {
      setNavigateToAdminPanel(true)
    } else {
      setOpen(true)
    }
  }

  const handleSave = (exit) => {
    const formatted = getFormattedCustomLabelsRequest(customizationData, personasConfig, filterConfig)
    setIsSaving(true)

    if (isLabelCustomized) {
      updateCustomizationLabels(formatted)
    }

    const updatePayload = {
      jurisdiction: selectedJurisdiction.map((element) => element.STATE_ID),
      dataAggregation: selectedDataAggregation.map((element) => element.key),
      filters: selectedFilters.map((f) => f.FILTER_KEY),
      usecase: selectedUseCases,
      benefits: selectedBenefits.map((element) => element.id),
      workforces: selectedWorkforces.map((element) => element.id),
      pointOfInterest: selectedPOI.map((element) => element.POI_ID),
      trendAnalysisFlag: selectedTrendAnalysis,
      userFeedbackFlag: selectedUserFeedback,
      chatbotFlag: selectedChatbot,
      chatbotExportFlag: selectedChatbotExport,
      chatbotCopyFlag: selectedChatbotCopy,
      exportFlag: selectedExportToggle,
      personaFlag: selectedPersona,
      closeExistingEventFlag
    }

    const updateDetails = {
      selectedJurisdiction,
      selectedFilters,
      exit,
      selectedTrendAnalysis,
      selectedUserFeedback,
      selectedExportToggle,
      selectedChatbot,
      selectedChatbotExport,
      selectedChatbotCopy,
      selectedPersona,
      updatePayload,
      setShowNotification,
      closeExistingEventFlag
    }

    handleUpdate(updateDetails, () => setOpen(true), setShowNotification, setNavigateToAdminPanel)
  }

  const isSaveDisabled = useMemo(
    () =>
      isSaving ||
      !isBaseDataReady ||
      !isHydrated ||
      showNotification ||
      saveButtonDisabled ||
      loadingUpdatedPermission ||
      errorUpdatedPermission ||
      filterDetailsError ||
      loadingHealthAndEquityConditions ||
      errorLoadingHealthAndEquityConditions ||
      loadingStates ||
      errorLoadingStates ||
      selectedPOI.length === 0,
    [
      isSaving,
      isBaseDataReady,
      isHydrated,
      showNotification,
      saveButtonDisabled,
      loadingUpdatedPermission,
      errorUpdatedPermission,
      filterDetailsError,
      loadingHealthAndEquityConditions,
      errorLoadingHealthAndEquityConditions,
      loadingStates,
      errorLoadingStates,
      selectedPOI
    ]
  )

  useEffect(() => {
    if (showNotification && !loadingUpdatedPermission && !updatedLabelLoading) {
      reloadFilters()
      setRefreshDashboard(true)
      setIsSaving(false)
      if (!errorUpdatedPermission && !updatedLabelError) {
        setIsLabelCustomized(false)
      }
    }
  }, [
    showNotification,
    loadingUpdatedPermission,
    updatedLabelLoading,
    errorUpdatedPermission,
    updatedLabelError,
    reloadFilters,
    setRefreshDashboard
  ])

  useEffect(() => {
    setSelectedUseCases([...getSelectedUseCases(updatedUseCases)])
  }, [updatedUseCases])

  useEffect(() => {
    if (!loadingUpdatedPermission && navigateToAdminPanel) {
      navigate('/adminpanel/manage-client-permissions')
    }
  }, [loadingUpdatedPermission, navigateToAdminPanel, navigate])

  useEffect(() => {
    if (!isBaseDataReady) {
      return
    }

    if (lastHydratedStateKey.current === initialStateKey && isHydrated) {
      return
    }

    setSelectedFilters(permissionedFilters)
    setSelectedJurisdiction(jurisdiction.filter((option) => currentJurisdiction.includes(option.STATE_ID)))
    setSelectedDataAggregation(
      dataAggregationConfig?.data?.filter((option) => currentDataAggregation.includes(option.key)) || []
    )
    setSelectedPOI(pointOfInterestsData.filter((option) => currentPOI.includes(Number.parseInt(option.POI_ID))) || [])
    setSelectedBenefits(
      benefits.filter(
        (option) => currentBenefits && currentBenefits.length > 0 && currentBenefits.includes(option.id)
      ) || []
    )
    setSelectedWorkforces(
      workforces.filter(
        (option) => currentWorkforces && currentWorkforces.length > 0 && currentWorkforces.includes(option.id)
      ) || []
    )
    setSelectedUseCases(currentUseCases || [])
    setSelectedTrendAnalysis(currentTrendAnalysisFlag)
    setSelectedUserFeedback(currentUserFeedbackFlag)
    setSelectedChatbot(currentChatbotFlag)
    setSelectedChatbotExport(currentChatbotExportFlag)
    setSelectedChatbotCopy(currentChatbotCopyFlag)
    setSelectedExportToggle(currentExportFlag)
    setSelectedPersona(currentPersonaFlag)

    lastHydratedStateKey.current = initialStateKey
    setIsHydrated(true)
  }, [
    initialStateKey,
    isBaseDataReady,
    isHydrated,
    currentJurisdiction,
    currentBenefits,
    currentWorkforces,
    currentUseCases,
    currentDataAggregation,
    currentTrendAnalysisFlag,
    currentUserFeedbackFlag,
    currentChatbotFlag,
    currentChatbotExportFlag,
    currentChatbotCopyFlag,
    currentExportFlag,
    currentPersonaFlag,
    currentPOI,
    permissionedFilters,
    jurisdiction,
    dataAggregationConfig,
    pointOfInterestsData,
    benefits,
    workforces
  ])

  useEffect(() => {
    const selectedFilterKeys = selectedFilters
      .map((f) => f.FILTER_KEY)
      .sort((a, b) => a.localeCompare(b))
      .toString()

    if (
      selectedFilters.length === 0 ||
      selectedJurisdiction.length === 0 ||
      selectedDataAggregation.length === 0 ||
      selectedPOI.length === 0
    ) {
      setSaveButtonDisabled(true)
    } else if (
      selectedFilterKeys !== permissionedFilterKeys ||
      selectedBenefitsChanged(selectedBenefits, currentBenefits) ||
      selectedWorkforcesChanged(selectedWorkforces, currentWorkforces) ||
      selectedJurisdictionChanged(selectedJurisdiction, currentJurisdiction) ||
      selectedDataAggregationChanged(selectedDataAggregation, currentDataAggregation) ||
      selectedUseCasesChanged(selectedUseCases, currentUseCases) ||
      selectedTrendAnalysisChanged(selectedTrendAnalysis, currentTrendAnalysisFlag) ||
      selectedUserFeedbackChanged(selectedUserFeedback, currentUserFeedbackFlag) ||
      selectedChatbotChanged(selectedChatbot, currentChatbotFlag) ||
      selectedChatbotExportChanged(selectedChatbotExport, currentChatbotExportFlag) ||
      selectedChatbotCopyChanged(selectedChatbotCopy, currentChatbotCopyFlag) ||
      selectedExportToggleChanged(selectedExportToggle, currentExportFlag) ||
      selectedPOIChanged(selectedPOI, currentPOI) ||
      selectedPersonaChanged(selectedPersona, currentPersonaFlag) ||
      isLabelCustomized
    ) {
      setSaveButtonDisabled(false)
    } else {
      setSaveButtonDisabled(true)
    }
  }, [
    selectedFilters,
    selectedJurisdiction,
    selectedBenefits,
    selectedWorkforces,
    selectedUseCases,
    selectedDataAggregation,
    selectedTrendAnalysis,
    selectedUserFeedback,
    selectedChatbot,
    selectedChatbotExport,
    selectedChatbotCopy,
    selectedExportToggle,
    selectedPersona,
    selectedPOI,
    currentBenefits,
    currentWorkforces,
    currentJurisdiction,
    currentDataAggregation,
    currentUseCases,
    currentTrendAnalysisFlag,
    currentUserFeedbackFlag,
    currentChatbotFlag,
    currentChatbotExportFlag,
    currentChatbotCopyFlag,
    currentExportFlag,
    currentPersonaFlag,
    currentPOI,
    permissionedFilterKeys,
    isLabelCustomized
  ])

  if (isSaving || loadingUpdatedPermission || updatedLabelLoading || !isBaseDataReady || !isHydrated) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='280px'>
        <CircularProgress title='My Permissions' />
      </Box>
    )
  }

  return (
    <Box display='flex' flexDirection='column' gap={4} pb={0}>
      <Box display='flex' flexDirection='column' gap={2}>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <StyledTitle>My Permissions</StyledTitle>
          <Button
            endIcon={areAllSectionsExpanded ? <UnfoldLessRoundedIcon /> : <UnfoldMoreRoundedIcon />}
            onClick={handleExpandAllToggle}
          >
            {areAllSectionsExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
        </Box>
        <Divider />
      </Box>

      <Box display='flex' flexDirection='column'>
        <SectionGroupHeader label='CONFERENCE SETTINGS' />
        <ProjectSectionAccordion
          title='User Feedback Mode'
          sectionKey='user-feedback-mode'
          expanded={expandedSections.userFeedbackMode}
          onChange={handleAccordionChange('userFeedbackMode')}
        >
          <UserFeedbackComponent
            setCloseExistingEventFlag={setCloseExistingEventFlag}
            selectedFeedback={selectedUserFeedback}
            setSelectedFeedback={setSelectedUserFeedback}
          />
        </ProjectSectionAccordion>

        <SectionGroupHeader label='GEOGRAPHIES' />
        <ProjectSectionAccordion
          title='Jurisdictions'
          sectionKey='jurisdictions'
          expanded={expandedSections.jurisdictions}
          onChange={handleAccordionChange('jurisdictions')}
        >
          <JurisdictionComponent
            selectedData={selectedJurisdiction}
            setJurisdictionValue={setSelectedJurisdiction}
            enabled={true}
            isCompleted={true}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Data Granularity'
          sectionKey='data-granularity'
          expanded={expandedSections.dataGranularity}
          onChange={handleAccordionChange('dataGranularity')}
        >
          <DataAggregation
            selectedData={selectedDataAggregation}
            setSelectedDataAggregation={setSelectedDataAggregation}
            isCompleted={true}
          />
        </ProjectSectionAccordion>

        <SectionGroupHeader label='FILTERS' />
        <ProjectSectionAccordion
          title='Individual Profile'
          sectionKey='individual-profile'
          expanded={expandedSections.individualProfile}
          onChange={handleAccordionChange('individualProfile')}
        >
          <IndividualProfileComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={true}
            handleOptionDeletion={handleOptionDeletion}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
            hasAnyFilters={hasAnyFilters}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Individual Health'
          sectionKey='individual-health'
          expanded={expandedSections.individualHealth}
          onChange={handleAccordionChange('individualHealth')}
        >
          <IndividualHealthFilterComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={true}
            handleOptionDeletion={handleOptionDeletion}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
            hasAnyFilters={hasAnyFilters}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Financial Wellbeing'
          sectionKey='financial-wellbeing'
          expanded={expandedSections.financialWellbeing}
          onChange={handleAccordionChange('financialWellbeing')}
        >
          <FinancialWellbeingComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={true}
            handleOptionDeletion={handleOptionDeletion}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
            hasAnyFilters={hasAnyFilters}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Home and Household'
          sectionKey='home-and-household'
          expanded={expandedSections.homeAndHousehold}
          onChange={handleAccordionChange('homeAndHousehold')}
        >
          <HomeAndHouseholdFilterComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={true}
            handleOptionDeletion={handleOptionDeletion}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
            hasAnyFilters={hasAnyFilters}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Resource Access Barriers and Support'
          sectionKey='resource-access-barriers'
          expanded={expandedSections.resourceAccessBarriers}
          onChange={handleAccordionChange('resourceAccessBarriers')}
        >
          <ResourceAccessBarriersComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={true}
            handleOptionDeletion={handleOptionDeletion}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
            hasAnyFilters={hasAnyFilters}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Community Support and Location'
          sectionKey='community-support'
          expanded={expandedSections.communitySupport}
          onChange={handleAccordionChange('communitySupport')}
        >
          <CommunitySupportComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={true}
            handleOptionDeletion={handleOptionDeletion}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
            hasAnyFilters={hasAnyFilters}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Environmental Exposure'
          sectionKey='environmental-exposure'
          expanded={expandedSections.environmentalExposure}
          onChange={handleAccordionChange('environmentalExposure')}
        >
          <EnvironmentalExposureComponent
            selectedData={selectedFilters}
            setFilterValue={setSelectedFilters}
            enabled={true}
            isCompleted={true}
            handleOptionDeletion={handleOptionDeletion}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
            hasAnyFilters={hasAnyFilters}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Point of Interests (POIs)'
          sectionKey='point-of-interests'
          expanded={expandedSections.pointOfInterests}
          onChange={handleAccordionChange('pointOfInterests')}
        >
          <PointOfInterest
            selectedData={selectedPOI}
            setPointofInterestValue={setSelectedPOI}
            enabled={true}
            isCompleted={true}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Benefit Programs'
          sectionKey='benefit-programs'
          expanded={expandedSections.benefitPrograms}
          onChange={handleAccordionChange('benefitPrograms')}
        >
          <Benefits benefits={benefits} selectedData={selectedBenefits} setSelectedData={setSelectedBenefits} />
        </ProjectSectionAccordion>
        {workforces && workforces.length > 0 && (
          <ProjectSectionAccordion
            title='Workforces'
            sectionKey='workforces'
            expanded={expandedSections.workforces}
            onChange={handleAccordionChange('workforces')}
          >
            <Workforces
              workforces={workforces}
              selectedData={selectedWorkforces}
              setSelectedData={setSelectedWorkforces}
            />
          </ProjectSectionAccordion>
        )}

        <SectionGroupHeader label='FEATURE PERMISSIONS' />
        <ProjectSectionAccordion
          title='Common Usecases'
          sectionKey='common-usecases'
          expanded={expandedSections.commonUsecases}
          onChange={handleAccordionChange('commonUsecases')}
        >
          <UseCases
            useCaseConfigData={useCaseConfigData}
            selectedFilters={selectedFilters}
            setUpdatedUseCases={setUpdatedUseCases}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Behavioral Persona'
          sectionKey='behavioral-persona'
          expanded={expandedSections.behavioralPersona}
          onChange={handleAccordionChange('behavioralPersona')}
        >
          <PersonaFlag
            selectedPersona={selectedPersona}
            setselectedPersona={setSelectedPersona}
            showCustomLabels={true}
            setCustomLabelDialogProps={setCustomLabelDialogProps}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Trend Analysis'
          sectionKey='trend-analysis'
          expanded={expandedSections.trendAnalysis}
          onChange={handleAccordionChange('trendAnalysis')}
        >
          <TrendAnalysisComponent selectedTrend={selectedTrendAnalysis} setselectedTrend={setSelectedTrendAnalysis} />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='Export'
          sectionKey='export'
          expanded={expandedSections.export}
          onChange={handleAccordionChange('export')}
        >
          <ExportPermissionComponent
            selectedExportToggle={selectedExportToggle}
            setselectedExportToggle={setSelectedExportToggle}
          />
        </ProjectSectionAccordion>
        <ProjectSectionAccordion
          title='HealthPrism Agent'
          sectionKey='healthprism-agent'
          expanded={expandedSections.healthprismAgent}
          onChange={handleAccordionChange('healthprismAgent')}
        >
          <ChatbotComponent selectedChat={selectedChatbot} setSelectedChatbot={setSelectedChatbot} />
          <ChatbotExport
            selectedExport={selectedChatbotExport}
            setSelectedExport={setSelectedChatbotExport}
            disabled={selectedChatbot?.[0] === 0}
          />
          <ChatbotCopy
            selectedCopy={selectedChatbotCopy}
            setSelectedCopy={setSelectedChatbotCopy}
            disabled={selectedChatbot?.[0] === 0}
          />
        </ProjectSectionAccordion>
      </Box>

      <Box
        display='flex'
        alignItems='center'
        position='fixed'
        bottom={0}
        left={0}
        right={0}
        zIndex={(theme) => theme.zIndex.appBar}
        bgcolor='background.paper'
        borderTop='1px solid'
        borderColor='divider'
        px={{xs: 2, md: 4}}
        py={2}
        ml={10}
        mr={2}
      >
        <Box display='flex' alignItems='center' ml={-1}>
          <SaveButton data-testid='save-button' onClick={() => handleSave(false)} disabled={isSaveDisabled}>
            Save
          </SaveButton>
          <SaveButton data-testid='save-exit-button' onClick={() => handleSave(true)} disabled={isSaveDisabled}>
            Save and Exit
          </SaveButton>
          <CancelButton variant='contrastButton' data-testid='cancel-button' sx={{ml: 1}} onClick={handleCancel}>
            Cancel
          </CancelButton>
        </Box>
      </Box>

      {open && (
        <CancelPopup
          open={open}
          setOpen={setOpen}
          setNavigateToAdminPanel={setNavigateToAdminPanel}
          updateCustomizationWithDefaultData={updateCustomizationWithDefaultData}
        />
      )}

      <SnackbarComponent
        error={updatedLabelError || errorUpdatedPermission}
        showNotification={showNotification}
        setShowNotification={setShowNotification}
        message={
          updatedLabelError || errorUpdatedPermission
            ? 'There is an error while updating the admin dashboard'
            : 'Admin Dashboard updated successfully'
        }
      />

      <ConfirmationDialog
        selectedUseCases={affectedUseCases.map((obj) => obj.name)}
        showDialog={showUseCaseConfirmationDialog}
        handleConfirm={handleConfirmOptionDeletion}
        handleClose={() => {
          setShowUseCaseConfirmationDialog(false)
        }}
      />

      <UseCaseNotificationAlert
        message={
          <StyledFlexBox>
            <StyledNotificationInfoOutlinedIcon />
            <StyledNotificationTextBox>
              {affectedUseCases.map((obj) => obj.name).join(', ')}{' '}
              {affectedUseCases.length > 1 ? 'use cases have' : 'use case has'} automatically been removed!
            </StyledNotificationTextBox>
          </StyledFlexBox>
        }
        vertical='top'
        horizontal='center'
        open={showInfoAlert}
        setOpen={setShowInfoAlert}
      />

      <CustomizationDialog
        setIsLabelCustomized={setIsLabelCustomized}
        data={customLabelDialogProps.data}
        open={customLabelDialogProps.open}
        section={customLabelDialogProps.section}
        initialExpandedRows={customLabelDialogProps.initialExpandedRows}
        handleClose={() => setCustomLabelDialogProps({...customLabelDialogProps, open: false})}
      />
    </Box>
  )
}

export default ManageMyPermissions
